import { Spacing } from '@persona-id/design-tokens';
import _ from 'lodash';
import { MouseEvent, ReactChild, useState } from 'react';
import styled from 'styled-components';

import LoadingOverlay from 'dashboard/components/LoadingOverlay';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Period } from 'dashboard/lib/unixTime';
import { fillChartData, generateDomain } from 'dashboard/lib/utils/charting';
import {
  DynamicFilters,
  MetricsGroupBy,
  VerificationStatusSummary,
  VerificationVolumeDatum,
} from 'dashboard/models/metrics-verification';
import {
  FilterRulesForType,
  VerificationMetricsState,
  VerificationTemplatesGroupedByType,
} from 'dashboard/redux/slices/metrics-verification';
import { ORDERED_VERIFICATION_TYPE_NAMES_SHORT } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/DashboardVerificationAnalyticsVolumeView';
import MetricsCard from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/MetricsCard';
import { GOVERNMENT_ID_DISPLAY_STRINGS, GovernmentIdClass } from 'lib/constants/governmentIds';
import { ApiModel, VerificationType } from 'lib/interfaces/api';

const VerificationMetricsContainer = styled.section``;

export const DropdownDivider = styled.div`
  margin-top: ${Spacing[2]};
  padding-top: ${Spacing[2]};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  width: 100%;
`;

export interface VerificationFilteredMetricProps
  extends Pick<
    VerificationMetricsState,
    | 'filteredVolumeSummary'
    | 'verificationVolumeOverTime'
    | 'lastQueryFilters'
    | 'filterRules'
    | 'volumeLastQueryFilterRules'
    | 'verificationGranularities'
  > {
  loading: boolean;
  barKeys: Partial<Record<keyof VerificationVolumeDatum, string>>;
  barsLabel?: string;
  lineKeys: Partial<Record<string, string>>;
  linesLabel?: string;
  handleAddAndRule: (type: FilteredVerificationType, orRuleIndex: number) => void;
  handleBulkRulesUpdate: (filterRules: Record<VerificationType, FilterRulesForType>) => void;
  handleUpdateAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => void;
  handleDeleteAndRule: (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
  ) => void;
  handleAddOrRule: (type: FilteredVerificationType) => void;
  handleGroupByUpdate: (type: FilteredVerificationType, dimension: FilterDimensions) => void;
  handleLoadMetrics: () => void;
  handleOpenFilters: (value: boolean) => void;
  handleVerificationTemplateFilterUpdate: (
    type: VerificationType,
    verificationTemplates: string[],
  ) => void;
  handleUpdateGranularity: (type: VerificationType, period: Period) => void;
  verificationTemplates: VerificationTemplatesGroupedByType;
  dynamicFilterOptions: DynamicFilters;
}

export interface DropdownItem {
  id: string;
  onClick?: (e?: MouseEvent) => void;
  label: ReactChild;
  name: string;
}

export type FilteredVerificationType =
  | ApiModel.VerificationGovernmentId
  | ApiModel.VerificationDatabase
  | ApiModel.VerificationSelfie
  | ApiModel.VerificationAamva;

export enum FilterDimensions {
  CountrySelect = 'country_select',
  CountryCode = 'country_code',
  IssuingSubdivision = 'issuing_subdivision',
  IssuingCountry = 'issuing_country',
  IdClass = 'id_type',
  CaptureMethod = 'capture_method',
  PlatformIntegration = 'platform',
  DeviceGroup = 'device_group',
  IssuingAuthority = 'issuing_authority',
  Clear = 'clear',
}

// TODO: [jonny] Remove this in the Filters PR by adding Enums
function titleize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).split('_').join(' ');
}

// Returns a government ID display string when we're using it
function translateIDType(str: GovernmentIdClass) {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return GOVERNMENT_ID_DISPLAY_STRINGS[str];
}

// If its a single ID type, translate it
function translateGroupValue(grp: string, dimension: string) {
  if (grp === 'NULL' || grp == null) {
    return '(Null)';
  }
  if (dimension === 'id_type') {
    return translateIDType(grp as GovernmentIdClass);
  } else return grp;
}

const LEGEND_HIGHLIGHT_THRESHOLD = 5;

export const PERIODICTY_MAP = {
  [Period.Day]: 'daily',
  [Period.Week]: 'weekly',
  [Period.Month]: 'monthly',
  [Period.Hour]: 'hourly',
};

const VerificationFilteredMetrics = ({
  barKeys,
  filterRules,
  filteredVolumeSummary,
  handleAddAndRule,
  handleAddOrRule,
  handleBulkRulesUpdate,
  handleDeleteAndRule,
  handleGroupByUpdate,
  handleLoadMetrics,
  handleOpenFilters,
  handleUpdateAndRule,
  handleUpdateGranularity,
  handleVerificationTemplateFilterUpdate,
  lastQueryFilters,
  lineKeys,
  loading,
  verificationTemplates,
  verificationGranularities,
  verificationVolumeOverTime,
  volumeLastQueryFilterRules,
  dynamicFilterOptions,
}: VerificationFilteredMetricProps) => {
  const [showFilterDropdown, setFilterDropdown] = useState(null);
  const [isOutsideClickEnabled, setIsOutsideClickEnabled] = useState(true);

  const groupItems: Record<FilteredVerificationType, DropdownItem[]> = {
    [ApiModel.VerificationGovernmentId]: [
      {
        id: FilterDimensions.CaptureMethod,
        label: (
          <>
            Capture method <HelpIcon>The method used to submit the ID</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationGovernmentId, FilterDimensions.CaptureMethod),
        name: 'Capture method',
      },
      {
        id: FilterDimensions.DeviceGroup,
        label: (
          <>
            Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationGovernmentId, FilterDimensions.DeviceGroup),
        name: 'Device group',
      },
      {
        id: FilterDimensions.IdClass,
        label: (
          <>
            ID class <HelpIcon>The detected type of Identification Document</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationGovernmentId, FilterDimensions.IdClass),
        name: 'ID class',
      },
      {
        id: FilterDimensions.IssuingCountry,
        label: (
          <>
            Issuing country <HelpIcon>The detected country that issued the ID</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationGovernmentId, FilterDimensions.IssuingCountry),
        name: 'Issuing country',
      },
      {
        id: FilterDimensions.IssuingSubdivision,
        label: (
          <>
            Issuing subdivision{' '}
            <HelpIcon>The detected state, province, or territory that issued the ID</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(
            ApiModel.VerificationGovernmentId,
            FilterDimensions.IssuingSubdivision,
          ),
        name: 'Issuing subdvision',
      },
      {
        id: FilterDimensions.PlatformIntegration,
        label: (
          <>
            Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(
            ApiModel.VerificationGovernmentId,
            FilterDimensions.PlatformIntegration,
          ),
        name: 'Platform integration',
      },
      {
        id: FilterDimensions.CountrySelect,
        label: (
          <>
            Selected country <HelpIcon>The Country chosen at the start of the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationGovernmentId, FilterDimensions.CountrySelect),
        name: 'Selected country',
      },
      {
        id: FilterDimensions.Clear,
        label: 'Clear',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick: () => handleGroupByUpdate(ApiModel.VerificationGovernmentId, null),
        name: 'Clear',
      },
    ],
    [ApiModel.VerificationSelfie]: [
      {
        id: 'capture_method',
        label: (
          <>
            Capture method <HelpIcon>The method used to submit the selfie</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationSelfie, FilterDimensions.CaptureMethod),
        name: 'Capture method',
      },
      {
        id: FilterDimensions.DeviceGroup,
        label: (
          <>
            Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationSelfie, FilterDimensions.DeviceGroup),
        name: 'Device group',
      },
      {
        id: FilterDimensions.PlatformIntegration,
        label: (
          <>
            Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationSelfie, FilterDimensions.PlatformIntegration),
        name: 'Platform integration',
      },
      {
        id: FilterDimensions.Clear,
        label: 'Clear',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick: () => handleGroupByUpdate(ApiModel.VerificationSelfie, null),
        name: 'Clear',
      },
    ],
    [ApiModel.VerificationDatabase]: [
      {
        id: 'country_code',
        label: (
          <>
            Country code{' '}
            <HelpIcon>The country provided by the user to run this verification</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationDatabase, FilterDimensions.CountryCode),
        name: 'Country code',
      },
      {
        id: FilterDimensions.DeviceGroup,
        label: (
          <>
            Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationDatabase, FilterDimensions.DeviceGroup),
        name: 'Device group',
      },
      {
        id: FilterDimensions.PlatformIntegration,
        label: (
          <>
            Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationDatabase, FilterDimensions.PlatformIntegration),
        name: 'Platform integration',
      },
      {
        id: FilterDimensions.Clear,
        label: 'Clear',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick: () => handleGroupByUpdate(ApiModel.VerificationDatabase, null),
        name: 'Clear',
      },
    ],
    [ApiModel.VerificationAamva]: [
      {
        name: 'Device group',
        id: FilterDimensions.DeviceGroup,
        label: (
          <>
            Device group <HelpIcon>The device category that was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationAamva, FilterDimensions.DeviceGroup),
      },
      {
        name: 'Issuing authority',
        id: FilterDimensions.IssuingAuthority,
        label: (
          <>
            Issuing authority <HelpIcon>The state that issued the Motor Vehicle document</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationAamva, FilterDimensions.IssuingAuthority),
      },
      {
        id: FilterDimensions.PlatformIntegration,
        label: (
          <>
            Platform integration <HelpIcon>The platform which was used during the flow</HelpIcon>
          </>
        ),
        onClick: () =>
          handleGroupByUpdate(ApiModel.VerificationAamva, FilterDimensions.PlatformIntegration),
        name: 'Platform integration',
      },
      {
        id: FilterDimensions.Clear,
        label: 'Clear',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick: () => handleGroupByUpdate(ApiModel.VerificationAamva, null),
        name: 'Clear',
      },
    ],
  };

  return (
    <VerificationMetricsContainer data-test='verification-metrics__verification-status-section'>
      {loading && <LoadingOverlay />}

      {(lastQueryFilters?.verificationTypes?.length === 0
        ? (Object.keys(ORDERED_VERIFICATION_TYPE_NAMES_SHORT) as VerificationType[])
        : (lastQueryFilters?.verificationTypes as VerificationType[])
      ).map((verificationType: VerificationType) => {
        const emptyData: VerificationStatusSummary = {
          created: 0,
          processed: 0,
          'pass-rate': 0,
          type: verificationType,
          passed: 0,
          'processed-rate': 0,
        };

        const data: VerificationStatusSummary =
          filteredVolumeSummary.data.find((summary) => {
            return summary.type === verificationType;
          }) || emptyData;

        // Is this particular Ver type grouped?
        const groupedVerificationType =
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          verificationType in lastQueryFilters.groupByDimension &&
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lastQueryFilters.groupByDimension[verificationType as FilteredVerificationType] != null;

        // Process the data differently if it is
        const dataForVerificationType = verificationVolumeOverTime.data[verificationType] ?? [];

        // TODO: [jonny] move this into the route
        let processedDataForVerificationType: VerificationVolumeDatum[] = [];

        if (groupedVerificationType) {
          for (let i = 0; i < dataForVerificationType.length; i++) {
            const currentElement = dataForVerificationType[i];
            const timeIndex = processedDataForVerificationType.findIndex((element) => {
              return element.created_timestamp === currentElement.created_timestamp;
            });

            const group = translateGroupValue(
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              currentElement.group,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              lastQueryFilters.groupByDimension[verificationType as FilteredVerificationType],
            );

            // If theres an existing element with the timestamp, add to the element
            if (timeIndex !== -1) {
              processedDataForVerificationType[timeIndex] = {
                ...processedDataForVerificationType[timeIndex],
                [group]: currentElement['pass-rate'],
                [`${group} volume`]: currentElement['processed'],
              };
            } else {
              // Otherwise append the current element
              processedDataForVerificationType.push({
                created_timestamp: currentElement.created_timestamp,
                [group]: currentElement['pass-rate'],
                [`${group} volume`]: currentElement['processed'],
              });
            }
          }
        } else {
          processedDataForVerificationType = dataForVerificationType;
        }

        // Get all of the Keys (Groups)
        const processedKeys: string[] = Array.from(
          new Set(
            processedDataForVerificationType
              .map((observation) => {
                return Object.keys(observation);
              })
              .flat(),
          ),
        );

        // Get the Top N (Default of 5) occurring keys for default filter
        const topGroups = dataForVerificationType.reduce(
          (topKeys: Record<string, number>, datum: VerificationVolumeDatum) => {
            const value = translateGroupValue(
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              datum.group,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              lastQueryFilters?.groupByDimension[verificationType as FilteredVerificationType],
            );
            if (!topKeys[value]) {
              topKeys[value] = 0;
            }

            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            topKeys[value] += datum.processed;
            return topKeys;
          },
          {},
        );

        // Get the top 5 kes and then create a volume list as well
        const orderedGroups = Object.entries(topGroups).sort((a, b) => b[1] - a[1]);
        const topValues = orderedGroups.slice(0, LEGEND_HIGHLIGHT_THRESHOLD).map((a) => a[0]);
        const topValuesVolume = topValues.map((a) => `${a} volume`);

        // Create a base value object + remove the timestamp
        const baseValue: Record<string, 0> = {};
        for (let i = 0; i < processedKeys.length; i++) {
          baseValue[processedKeys[i]] = 0;
        }
        delete baseValue['created_timestamp'];

        const effectivePeriod = verificationGranularities[verificationType];

        const filledProcessedDataForVerificationType = fillChartData(
          processedDataForVerificationType,
          baseValue,
          generateDomain(
            [
              // These are in MS, need to divide
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              Date.parse(lastQueryFilters?.createdAtStart) / 1000,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              Date.parse(lastQueryFilters?.createdAtEnd) / 1000,
            ],
            verificationGranularities[verificationType],
          ),
          effectivePeriod,
          true,
          true,
        );

        const processedLineKeys: Record<string, string> = groupedVerificationType
          ? _.keys(
              _.countBy(dataForVerificationType, function (datum) {
                return translateGroupValue(
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  datum.group,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  lastQueryFilters?.groupByDimension[verificationType as FilteredVerificationType],
                );
              }),
            ).reduce(function (r, a) {
              r[a] = titleize(a) || '';
              return r;
            }, Object.create(null))
          : {};

        // Order the line keys based off of the highest volume
        const orderedProcessedLineKeys: Record<string, string> = {};
        orderedGroups.forEach((group) => {
          orderedProcessedLineKeys[group[0]] = processedLineKeys[group[0]];
        });

        // Iterate through the lineKeys then just get the values + volume
        // Order propagates to bar keys
        const processedBarKeys: Record<string, string> = {};
        for (const key in orderedProcessedLineKeys) {
          processedBarKeys[`${key} volume`] = processedLineKeys[key];
        }

        const verificationTemplatesOptionsForType = verificationTemplates[verificationType];

        // If there are options for this type, check if there's a none option and then append some copy to it
        if (verificationTemplatesOptionsForType != null) {
          const legacyTemplate = verificationTemplatesOptionsForType.find(
            (template) => template.id == null,
          );

          if (legacyTemplate != null) {
            legacyTemplate.tooltipText = 'Verifications that do not have an associated template';
          }
        }

        const groupItemsForType = groupItems[verificationType as FilteredVerificationType] || [];
        const filterRulesForType = filterRules[verificationType];
        const groupByDimensionForType =
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          lastQueryFilters.groupByDimension[verificationType as FilteredVerificationType];
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const groupedByInquiries = lastQueryFilters['groupBy'] === MetricsGroupBy.Inquiry;

        return (
          <MetricsCard
            barKeys={barKeys}
            data={data}
            emptyData={emptyData}
            filledProcessedDataForVerificationType={filledProcessedDataForVerificationType}
            filterOrRulesForType={filterRulesForType.or}
            groupByDimensionForType={groupByDimensionForType}
            groupItemsForType={groupItemsForType}
            groupedByInquiries={groupedByInquiries}
            groupedVerificationType={groupedVerificationType}
            handleAddAndRule={handleAddAndRule}
            handleAddOrRule={handleAddOrRule}
            handleBulkRulesUpdate={handleBulkRulesUpdate}
            handleDeleteAndRule={handleDeleteAndRule}
            handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
            handleLoadMetrics={handleLoadMetrics}
            handleOpenFilters={handleOpenFilters}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            handleUpdateAndRule={handleUpdateAndRule}
            handleUpdateGranularity={handleUpdateGranularity}
            isOutsideClickEnabled={isOutsideClickEnabled}
            key={`verification-analytics-metric-card-${verificationType}`}
            lineKeys={lineKeys}
            orderedProcessedLineKeys={orderedProcessedLineKeys}
            processedBarKeys={processedBarKeys}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setFilterDropdown={setFilterDropdown}
            selectedVerificationTemplates={filterRulesForType.verificationTemplateIds}
            setIsOutsideClickEnabled={setIsOutsideClickEnabled}
            showFilterDropdown={showFilterDropdown}
            topValues={topValues}
            topValuesVolume={topValuesVolume}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            verificationTemplatesOptionsForType={verificationTemplatesOptionsForType}
            verificationType={verificationType}
            verificationVolumeOverTime={verificationVolumeOverTime}
            lastQueryFilters={lastQueryFilters}
            volumeLastQueryFilterRules={volumeLastQueryFilterRules}
            dynamicFilterOptions={dynamicFilterOptions}
            granularityOption={effectivePeriod}
          />
        );
      })}
    </VerificationMetricsContainer>
  );
};

export default VerificationFilteredMetrics;
